import type { MouseEvent, ReactElement } from 'react';

import styles from './SkipLink.module.css';

/**
 * "Skip to the editor" — recon §1.1, docs/DESIGN.md §7.4.
 *
 * The first focusable thing on the page and, until it is focused, invisible. Two bars of
 * chrome is not much to tab through, but a keyboard user should not have to discover that by
 * tabbing through it, and on this screen the editor IS the page.
 *
 * ═══ WHY THERE IS AN onClick ON AN ANCHOR THAT ALREADY HAS AN href ═══
 *
 * `href="#editor"` alone scrolls to the target and updates the URL, but it moves DOM FOCUS
 * only if the target is focusable. `<main>` is not, so the next Tab would continue from the
 * skip link — i.e. straight back into the header — and the link would silently do nothing of
 * what its name promises. `AppShell` gives `<main>` `tabIndex={-1}` and this handler focuses
 * it explicitly. Both halves are required; neither is sufficient.
 *
 * `preventDefault` is deliberately NOT called: the hash in the address bar is a legitimate
 * anchor, and `scrollIntoView` behaviour differs across engines in ways the browser's own
 * fragment navigation does not.
 */

export interface SkipLinkProps {
  /** Localised, `action.skipToEditor`. */
  readonly label: string;
  /** The `<main>` id. Matches `AppShell`'s `mainId`, which defaults to the same word. */
  readonly targetId?: string;
}

export function SkipLink({ label, targetId = 'editor' }: SkipLinkProps): ReactElement {
  const onClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    const target = event.currentTarget.ownerDocument.getElementById(targetId);
    target?.focus();
  };

  return (
    <a className={styles.link} href={`#${targetId}`} onClick={onClick}>
      {label}
    </a>
  );
}

export default SkipLink;
